import { useApi, PaginatedResponse } from '@/api';
import { Link, TableCellBottom, HyperLiquidLink } from '@/components';
import {
  CurrencyValue,
  DataTable,
  Flex,
  RelativeDateTime,
  WalletAddress,
  TableSkeleton
} from '@blockanalitica/ui';
import {
  TablePagination,
  TableSorting
} from '@blockanalitica/ui/package/types/src/components/atoms/Table/types';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';

type EventsTableProps = {
  pagination: TablePagination;
  sorting: TableSorting;
  eventType: string | null;
  marketAddress: string;
  date: string;
};

type ActivityData = {
  wallet_address: string;
  tx_hash: string;
  order_index: string;
  datetime: string;
  block_number: number;
  event: string;
  amount: string | null;
  amount_usd: string | null;
  symbol: string;
};

export default function EventsTable({
  marketAddress,
  pagination,
  sorting,
  eventType,
  date
}: EventsTableProps) {
  const [data, error] = useApi<PaginatedResponse<ActivityData[]>>({
    url: `/markets/${marketAddress}/activity/`,
    queryParams: {
      page: pagination.currentPage,
      limit: pagination.pageSize,
      sort: sorting.currentSorting,
      event_type: eventType,
      date: date
    }
  });

  if (error) {
    throw error;
  }

  if (!data) {
    return <TableSkeleton rows={10} columns={6} />;
  }

  const columns: TableColumn[] = [
    {
      header: '',
      getCell: (row) => (
        <Link to={`/wallets/${row.wallet_address}`}>
          <WalletAddress address={row.wallet_address} />
        </Link>
      )
    },
    {
      header: 'Event',
      getCell: (row) => row.event
    },
    {
      header: 'Amount',
      getCell: (row) => (
        <CurrencyValue
          value={row.amount}
          options={{
            trend: true,
            trendIcon: false
          }}
        />
      ),
      getCellBottom: (row) => (
        <TableCellBottom checkValue={row.amount_usd}>
          <CurrencyValue
            value={row.amount_usd}
            options={{
              currency: 'USD',
              absolute: true
            }}
          />
        </TableCellBottom>
      ),
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'amount'
    },
    {
      header: 'Time',
      getCell: (row) => (
        <Flex gap="xs" color="muted">
          <RelativeDateTime value={row.datetime} />
          <HyperLiquidLink path={`tx/${row.tx_hash}`} />
        </Flex>
      ),
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'order_index'
    }
  ];

  return (
    <DataTable
      rows={data.results}
      columns={columns}
      getKey={(row) => `${row.wallet_address}${row.order_index}`}
      sorting={sorting}
      pagination={{
        ...pagination,
        totalRecords: data.pagination.total
      }}
    />
  );
}
